import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';


export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  // Skip auth for URLs containing 's3' (case-insensitive)
  const shouldSkipAuth = req.url.toLowerCase().includes('s3');

  if (shouldSkipAuth) {
    return next(req);
  }

  let isRefreshing = false;
  const refreshTokenSubject = new BehaviorSubject<string | null>(null);

  function handle401Error(request: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);
      return authService.refreshToken().pipe(
        switchMap((token: string) => {
          isRefreshing = false;
          refreshTokenSubject.next(token);
          const authReq = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
              ...(request.headers.get('Content-Type') ? {} : { 'Content-Type': 'application/json' })
            }
          });
          return next(authReq);
        }),
        catchError(err => {
          isRefreshing = false;
          authService.logout();
          return throwError(() => err);
        })
      );
    } else {
      return refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          const authReq = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
              ...(request.headers.get('Content-Type') ? {} : { 'Content-Type': 'application/json' })
            }
          });
          return next(authReq);
        })
      );
    }
  }

  return authService.getAccessToken().pipe(
    switchMap(token => {
      let authReq = req;
      if (token) {
        authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            ...(req.headers.get('Content-Type') ? {} : { 'Content-Type': 'application/json' })
          }
        });
      }
      return next(authReq);
    }),
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handle401Error(req);
      }
      return throwError(() => error);
    })
  );
};